<template>
  <div class="servicemode">
    <!-- UI Messages: Errors, Information -->
    <div class="success" v-if="addNewTechnicianSuccess">
      <h3>{{ $t('Web.service_handover_success_header') }}</h3>
      <p>{{ $t('Web.ServiceMode.Messages.Success') }}</p>
    </div>

    <div class="success" v-if="revokeTechnicianSuccess">
      <h3>{{ $t('Web.service_handover_success_header') }}</h3>
      <p>{{ $t('Web.service_mode_revoke_success', [revokedTechnician]) }}</p>
    </div>

    <div class="error" v-if="revokeTechnicianError">
      <p>{{ $t('Web.service_mode_revoke_fail', [revokedTechnician]) }}</p>
    </div>

    <div class="error" v-if="addNewTechnicianError">
      <p>{{ $t(specificErrorMessage, [revokedTechnician]) }}</p>
    </div>
    <!-- /UI Messages: Errors, Information -->

    <div v-if="addNewTechnician">
      <div
        v-if="(assignedTechnicians || []).length > 0"
        class="back"
        slot="body"
        @click="addNewTechnicianClicked = false"
      >
        <i class="icon icon--back"></i>
      </div>

      <div class="textContent" v-if="!addNewTechnicianError">
        <p>{{ $t('Web.ServiceMode.Description') }}</p>
      </div>

      <form accept-charset="UTF-8" @submit.stop.prevent="assignToTechnician" autocomplete="on">
        <input
          id="serviceModeMail"
          class="form-control mail-margin"
          @keypress.enter.prevent="assignToTechnician"
          @keypress.space.prevent=""
          placeholder="E-Mail"
          name="email"
          type="email"
          v-model="email"
          v-validate="'required'"
          autofocus
        />

        <div class="button-wrapper">
          <cactus-button :class="{ disabled: !validateMail }" @btnClicked="assignToTechnician">
            {{ $t('Web.ServiceMode.Buttons.Request') }}
          </cactus-button>
        </div>
      </form>

      <div v-if="revokeTechnicianError || specificErrorMessage">
        <ServicePerson></ServicePerson>
      </div>
    </div>

    <div v-else>
      <div class="textContent">
        <p>{{ $t('Web.service_mode_list') }}</p>
      </div>

      <div v-if="assignedTechnicians && (assignedTechnicians || []).length > 0">
        <div class="error" v-if="revokeTechnicianError">
          <p>{{ $t(specificErrorMessage) }}</p>
        </div>

        <ul>
          <li class="technician" v-for="(el, index) in assignedTechnicians" :key="index">
            <div class="technician--profileData">
              <span>{{ el.profile.email }}</span>
              <span>{{ el.profile.first_name }} {{ el.profile.last_name }}</span>
            </div>
            <div class="technician--revoke">
              <button @click="revokeAccess(el.profile.email)">
                {{ $t('Web.ServiceMode.Buttons.withdraw') }}
              </button>
            </div>
          </li>
        </ul>
      </div>

      <!-- Error: Add new technician  -->
      <div class="service-person" v-if="addNewTechnicianError">
        <service-person />
      </div>
      <!-- /Error: Add new technician  -->

      <!-- Submit Button -->
      <cactus-button
        class="addTechnician"
        :isPrimary="true"
        @btnClicked="
          addNewTechnicianClicked = true;
          resetHints();
        "
      >
        {{ $t('Web.ServiceMode.Buttons.Request') }}
      </cactus-button>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import CactusButton from '../_common/cactusButton';
import ServicePerson from '../_common/servicePerson';

export default {
  name: 'ServiceMode',
  components: {
    CactusButton,
    ServicePerson
  },
  props: {
    deviceSerial: {
      type: String,
      default: undefined,
      required: true
    }
  },
  data() {
    return {
      email: '',
      addNewTechnicianError: false,
      addNewTechnicianSuccess: false,
      revokeTechnicianError: false,
      revokeTechnicianSuccess: false,
      status: 0,
      errorMessageBase: 'Web.ServiceMode.Errors.',
      specificErrorMessage: '',
      addNewTechnicianClicked: false,
      assignToTechnicianInProgress: false,
      revokedTechnician: ''
    };
  },
  created() {
    this.$showGlobalLoadingSpinner();
    this.getTechnicianList({ serial: this.deviceSerial }).finally(() => {
      this.$hideGlobalLoadingSpinner();
    });
  },
  methods: {
    ...mapActions(['assignDeviceToTechnician', 'getTechnicianList', 'revokeTechnicianAccess']),

    resetHints() {
      this.specificErrorMessage = '';
      this.addNewTechnicianError = false;
      this.addNewTechnicianSuccess = false;
      this.revokeTechnicianError = false;
      this.revokeTechnicianSuccess = false;
    },

    revokeAccess(technicianMail) {
      if (!this.deviceSerial || !technicianMail) return;
      this.resetHints();
      const requestObj = {
        serial: this.deviceSerial,
        technicianMail: technicianMail.toLowerCase()
      };

      this.$showGlobalLoadingSpinner();
      this.revokeTechnicianAccess(requestObj)
        .then(() => {
          this.revokeTechnicianSuccess = true;
          this.revokedTechnician = technicianMail;
          return this.getTechnicianList({ serial: this.deviceSerial });
        })
        .catch(() => {
          this.revokeTechnicianError = true;
          this.revokedTechnician = technicianMail;
        })
        .finally(() => {
          this.$hideGlobalLoadingSpinner();
        });
    },

    assignToTechnician() {
      if (!this.validateMail || !this.deviceSerial || this.assignToTechnicianInProgress) return;

      const requestObj = {
        serial: this.deviceSerial,
        technicianMail: this.email.toLowerCase()
      };

      this.assignToTechnicianInProgress = true;
      this.resetHints();

      this.$showGlobalLoadingSpinner();
      this.assignDeviceToTechnician(requestObj)
        .then(() => {
          this.addNewTechnicianSuccess = true;
          return this.getTechnicianList({ serial: this.deviceSerial });
        })
        .catch(err => {
          try {
            this.specificErrorMessage = this.errorMessageBase + err.response.status;
          } catch (error) {
            this.specificErrorMessage = err.message || this.errorMessageBase + '500';
          }
          this.addNewTechnicianError = true;
          this.addNewTechnicianSuccess = false;
        })
        .finally(() => {
          this.assignToTechnicianInProgress = false;
          this.addNewTechnicianClicked = false;
          this.$hideGlobalLoadingSpinner();
        });
    }
  },
  computed: {
    ...mapGetters(['assignedTechnicians']),

    validateMail() {
      // Regex to check email address format according to RFC 5322 Official Standard
      // eslint-disable-next-line
      return /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
        this.email
      );
    },

    addNewTechnician() {
      if ((this.assignedTechnicians || []).length > 0 && !this.addNewTechnicianClicked) {
        return false;
      }
      if (this.addNewTechnicianClicked) {
        return true;
      }
      return true;
    }
  }
};
</script>

<style lang="scss" scoped>
.back {
  position: relative;
  top: 20px;
  left: 10px;
  white-space: nowrap;
  cursor: pointer;
  margin-bottom: 20px;

  .icon--back {
    display: inline-block;
    width: 24px;
    height: 24px;
    background: url('../../assets/img/ico_header_back.svg') no-repeat center center;
  }
}

.technician {
  margin-top: 10px;
  border-bottom: 1px solid $cloudy-blue;
  display: flex;
  padding-bottom: 10px;
  justify-content: center;

  &--profileData {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: self-start;
    max-width: 75%;

    span:first-child {
      @include textCharts;
      color: $petrol;
      display: block;
      margin-bottom: 4px;
      word-wrap: break-word;
      max-width: 80%;
    }

    span {
      @include textCharts;
      color: $charcoal-grey;
    }
  }

  &--revoke {
    justify-self: end;

    button {
      width: auto !important;
      background-color: $greeny-blue;
      color: white;
    }
  }
}

.servicemode {
  margin: 0 auto 40px auto;
  max-width: $maxWidth;

  .success {
    text-align: center;
    padding: 50px 0 24px 0;

    h3 {
      color: $orange;
      margin-bottom: 15px;
    }

    p {
      font-size: 13px;
      line-height: 1.38;
    }
  }

  .textContent {
    padding: 50px 0 24px 0;

    h3 {
      margin-bottom: 15px;
    }

    p {
      font-size: 14px;
      line-height: 1.38;
      color: $charcoal-grey;
    }
  }

  .addTechnician {
    margin-top: 50px;
    text-align: center;
  }

  form {
    margin: 0 ($maxWidth * 0.05);
  }

  input {
    height: 48px;
    width: $maxWidth * 0.9;
    padding: 0 36px;
    background-color: #fff;
    font-size: 14px;
    color: $petrol;
    box-sizing: border-box;

    &:only-of-type {
      border: 1px solid $cloudy-blue;
      border-radius: 8px;
      box-shadow: 0 0 4px 1px rgba(0, 0, 0, 0.1);
    }

    &.mail-margin {
      margin-bottom: 24px;
    }
  }

  .service-person {
    margin-top: 28px;
    margin-top: 2rem;
  }
}
</style>
